import Cookies from "js-cookie";
import type { User } from "@/types/user";

// Optional shared parent domain for cross-subdomain cookies (e.g. ".bsg.business").
// Empty/undefined ⇒ host-only cookies (correct for localhost dev).
const COOKIE_DOMAIN = import.meta.env.VITE_COOKIE_DOMAIN || undefined;

// Cookie configuration
const COOKIE_OPTIONS = {
  expires: 7, // 7 days
  secure: import.meta.env.PROD, // HTTPS only in production
  sameSite: "strict" as const,
  path: "/",
  ...(COOKIE_DOMAIN ? { domain: COOKIE_DOMAIN } : {}),
};

// js-cookie only deletes a cookie when the remove call matches the attributes
// it was written with — so removals must carry the same path AND domain.
const COOKIE_REMOVE_OPTIONS = {
  path: "/",
  ...(COOKIE_DOMAIN ? { domain: COOKIE_DOMAIN } : {}),
};

const ACCESS_TOKEN_KEY = "access_token";
const USER_DATA_KEY = "user_data";

export const authCookies = {
  // Access token management
  setAccessToken: (token: string) => {
    Cookies.set(ACCESS_TOKEN_KEY, token, COOKIE_OPTIONS);
  },

  getAccessToken: (): string | null => {
    return Cookies.get(ACCESS_TOKEN_KEY) || null;
  },

  removeAccessToken: () => {
    Cookies.remove(ACCESS_TOKEN_KEY, COOKIE_REMOVE_OPTIONS);
  },

  // Legacy token methods (for backward compatibility)
  setToken: (token: string) => {
    authCookies.setAccessToken(token);
  },

  getToken: (): string | null => {
    return authCookies.getAccessToken();
  },

  removeToken: () => {
    authCookies.removeAccessToken();
  },

  // User data management
  setUser: (user: User) => {
    try {
      const userData = JSON.stringify(user);
      Cookies.set(USER_DATA_KEY, userData, COOKIE_OPTIONS);
    } catch (error) {
      console.error("Failed to store user data in cookies:", error);
    }
  },

  getUser: (): User | null => {
    try {
      const userData = Cookies.get(USER_DATA_KEY);
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      console.error("Failed to parse user data from cookies:", error);
      // Clear invalid cookie data
      Cookies.remove(USER_DATA_KEY, COOKIE_REMOVE_OPTIONS);
      return null;
    }
  },

  removeUser: () => {
    Cookies.remove(USER_DATA_KEY, COOKIE_REMOVE_OPTIONS);
  },

  // Clear all client-managed auth cookies.
  // The Strapi-managed HttpOnly refreshToken cookie is cleared server-side on logout.
  clearAll: () => {
    Cookies.remove(ACCESS_TOKEN_KEY, COOKIE_REMOVE_OPTIONS);
    Cookies.remove(USER_DATA_KEY, COOKIE_REMOVE_OPTIONS);
  },

  // Check if authentication cookies exist
  hasAuthData: (): boolean => {
    const accessToken = Cookies.get(ACCESS_TOKEN_KEY);
    const userData = Cookies.get(USER_DATA_KEY);
    return !!(accessToken && userData);
  },

  // Get all auth data at once
  getAuthData: (): {
    accessToken: string | null;
    user: User | null;
    // Legacy compatibility
    token: string | null;
  } => {
    return {
      accessToken: authCookies.getAccessToken(),
      user: authCookies.getUser(),
      token: authCookies.getAccessToken(), // Legacy compatibility
    };
  },

  // Set all auth data at once
  setAuthData: (accessToken: string, user: User) => {
    authCookies.setAccessToken(accessToken);
    authCookies.setUser(user);
  },

  // Set access token
  setTokens: (accessToken: string) => {
    authCookies.setAccessToken(accessToken);
  },

  // Get access token
  getTokens: (): {
    accessToken: string | null;
  } => {
    return {
      accessToken: authCookies.getAccessToken(),
    };
  },
};

// Utility for cookie-based persistence middleware
export const cookieStorage = {
  getItem: (name: string): string | null => {
    return Cookies.get(name) || null;
  },
  setItem: (name: string, value: string): void => {
    Cookies.set(name, value, COOKIE_OPTIONS);
  },
  removeItem: (name: string): void => {
    Cookies.remove(name, COOKIE_REMOVE_OPTIONS);
  },
};

// Enhanced storage for Zustand persist middleware
export const createCookieStorage = () => ({
  getItem: (name: string): Promise<string | null> => {
    try {
      const value = Cookies.get(name);
      return Promise.resolve(value || null);
    } catch (error) {
      console.error("Error reading from cookies:", error);
      return Promise.resolve(null);
    }
  },
  setItem: (name: string, value: string): Promise<void> => {
    try {
      Cookies.set(name, value, COOKIE_OPTIONS);
      return Promise.resolve();
    } catch (error) {
      console.error("Error writing to cookies:", error);
      return Promise.resolve();
    }
  },
  removeItem: (name: string): Promise<void> => {
    try {
      Cookies.remove(name, COOKIE_REMOVE_OPTIONS);
      return Promise.resolve();
    } catch (error) {
      console.error("Error removing from cookies:", error);
      return Promise.resolve();
    }
  },
});
